import * as React from 'react';
import { Plus, X } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../primitives/popover';
import { cn } from '../../lib/utils';
import { describeFilter, activeFilterKeys } from './describeFilter';
import { FilterEditor } from './FilterEditor';
import type { AppliedFilters, FilterField, FilterValue } from './types';

export interface FilterBuilderProps {
  fields: FilterField[];
  filters: AppliedFilters;
  onChange: (_filters: AppliedFilters) => void;
  className?: string;
}

/**
 * Filtering as a row of chips, each reading field · operator · value.
 *
 * One mechanism covers every dimension, so filter state lives in one place and
 * says what it is in words. The alternative shapes — a sheet listing every
 * dimension, or a select per dimension — either hide the applied state behind
 * an overlay or run out of room past about four fields.
 *
 * Adding a filter happens inside a single popover: pick the field, and that
 * same surface becomes the field's value editor. Handing off from a menu to a
 * separate popover meant two overlapping layers competing over focus, which
 * is both fragile and a visible flash.
 */
export function FilterBuilder({ fields, filters, onChange, className }: FilterBuilderProps) {
  const [addOpen, setAddOpen] = React.useState(false);
  // Which field the add-popover is currently editing; null means it is still
  // showing the field list.
  const [addingKey, setAddingKey] = React.useState<string | null>(null);
  const [editingKey, setEditingKey] = React.useState<string | null>(null);

  const activeKeys = activeFilterKeys(fields, filters);
  const availableFields = fields.filter((field) => !activeKeys.includes(field.key));
  const addingField = fields.find((field) => field.key === addingKey);

  const closeAdd = () => {
    setAddOpen(false);
    setAddingKey(null);
  };

  const removeFilter = (key: string) => {
    const { [key]: _removed, ...rest } = filters;
    onChange(rest);
    setEditingKey((current) => (current === key ? null : current));
  };

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {activeKeys.map((key) => {
        const field = fields.find((candidate) => candidate.key === key)!;
        const described = describeFilter(field, filters[key]!)!;
        return (
          <span
            key={key}
            data-testid={`filter-chip-${key}`}
            className="inline-flex items-center rounded-full border border-primary bg-primary/10 text-xs text-primary"
          >
            <Popover
              open={editingKey === key}
              onOpenChange={(open) => setEditingKey(open ? key : null)}
            >
              <PopoverTrigger asChild>
                <button type="button" className="py-1 pl-3 pr-1.5">
                  {field.label} <b className="font-semibold">{described.operator}</b>{' '}
                  {described.value}
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0">
                <FilterEditor
                  field={field}
                  value={filters[key]}
                  onApply={(value: FilterValue) => onChange({ ...filters, [key]: value })}
                  onCommit={() => setEditingKey(null)}
                  onClear={() => removeFilter(key)}
                />
              </PopoverContent>
            </Popover>
            <button
              type="button"
              aria-label={`Remove ${field.label} filter`}
              data-testid={`filter-remove-${key}`}
              onClick={() => removeFilter(key)}
              className="rounded-full p-1 pr-2 hover:text-primary/70"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        );
      })}

      {availableFields.length > 0 && (
        <Popover
          open={addOpen}
          onOpenChange={(open) => (open ? setAddOpen(true) : closeAdd())}
        >
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="filter-add"
              className="inline-flex items-center gap-1 rounded-full border border-dashed border-primary px-3 py-1 text-xs text-primary transition-colors hover:bg-primary/5"
            >
              <Plus className="h-3 w-3" />
              Filter
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0">
            {addingField ? (
              // The value has to be fed back in while the filter is being
              // built, not only once it has a chip: without it a multi-select
              // editor always saw an empty selection, so ticking a second
              // option replaced the first instead of adding to it.
              <FilterEditor
                field={addingField}
                value={filters[addingField.key]}
                onApply={(value: FilterValue) => onChange({ ...filters, [addingField.key]: value })}
                onCommit={closeAdd}
                // Clear during the add flow abandons the filter — it now has a
                // value written into it, so simply closing would leave a chip
                // behind for something that was cancelled.
                onClear={() => {
                  removeFilter(addingField.key);
                  closeAdd();
                }}
              />
            ) : (
              <div className="min-w-[11rem] p-1">
                {availableFields.map((field) => (
                  <button
                    key={field.key}
                    type="button"
                    data-testid={`filter-field-${field.key}`}
                    onClick={() => setAddingKey(field.key)}
                    className="flex w-full items-center rounded-sm px-2.5 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
                  >
                    {field.icon && <field.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                    {field.label}
                  </button>
                ))}
              </div>
            )}
          </PopoverContent>
        </Popover>
      )}

      {activeKeys.length > 0 && (
        <button
          type="button"
          data-testid="filter-clear-all"
          onClick={() => onChange({})}
          className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
        >
          Clear all
        </button>
      )}
    </div>
  );
}
